/**
 * Film Brain — Frontend service.
 *
 * The Brain is 1895's living context graph: derive it from the script, read the
 * graph + resolved per-scene context, author decisions, run gap detection +
 * readiness, and pull derived deliverables. Auth rides the global axios Bearer
 * interceptor (AuthContext), so these calls need no extra wiring.
 */
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/** Start (or re-start) derivation. Returns { run_id, status }. */
export async function deriveBrain(projectId, { enrich = true } = {}) {
  const { data } = await axios.post(`${API}/brain/${projectId}/derive`, { enrich });
  return data;
}

/** Poll a derivation run. */
export async function getBrainRun(projectId, runId) {
  const { data } = await axios.get(`${API}/brain/${projectId}/run/${runId}`);
  return data;
}

export async function getBrainRuns(projectId) {
  const { data } = await axios.get(`${API}/brain/${projectId}/runs`);
  return data;
}

/** Dashboard overview: counts, latest run, readiness. */
export async function getBrainOverview(projectId) {
  const { data } = await axios.get(`${API}/brain/${projectId}`);
  return data;
}

export async function getBrainGraph(projectId) {
  const { data } = await axios.get(`${API}/brain/${projectId}/graph`);
  return data;
}

/** Resolved canonical context for one scene. */
export async function getSceneContext(projectId, sceneNumber) {
  const { data } = await axios.get(`${API}/brain/${projectId}/scene/${sceneNumber}`);
  return data;
}

export async function getBrainNode(projectId, nodeId) {
  const { data } = await axios.get(`${API}/brain/${projectId}/node/${encodeURIComponent(nodeId)}`);
  return data;
}

/** Apply authored decisions (costume / casting / time / style). ops: [{kind,...}]. */
export async function authorBrain(projectId, ops) {
  const { data } = await axios.post(`${API}/brain/${projectId}/authored`, { ops });
  return data;
}

/** List gaps. Optional role/status filters. */
export async function getGaps(projectId, { role, status = 'open' } = {}) {
  const params = {};
  if (role) params.role = role;
  if (status) params.status = status;
  const { data } = await axios.get(`${API}/brain/${projectId}/gaps`, { params });
  return data;
}

export async function scanGaps(projectId) {
  const { data } = await axios.post(`${API}/brain/${projectId}/gaps/scan`, {});
  return data;
}

/** Resolve a gap with a value, or dismiss it. */
export async function resolveGap(projectId, gapKey, { value = null, dismiss = false } = {}) {
  const { data } = await axios.post(
    `${API}/brain/${projectId}/gaps/${encodeURIComponent(gapKey)}/resolve`,
    { value, dismiss },
  );
  return data;
}

export async function getReadiness(projectId) {
  const { data } = await axios.get(`${API}/brain/${projectId}/readiness`);
  return data;
}

export async function getBrainView(projectId, view) {
  // view: 'shotlist' | 'breakdown' | 'treatment'
  const { data } = await axios.get(`${API}/brain/${projectId}/views/${view}`);
  return data;
}
